import { z } from 'zod';
import { MeResponse } from './generated/backend/authentication/authentication.schema';

export interface AuthStore {
  // Flags for initializing the AuthStore
  isInitialized: boolean;
  isInitializing: boolean;
  // State
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  user: AuthUser | null;
  /** True only when an existing session could not be recovered (cookie expired/invalid). False on first visit. */
  sessionExpired: boolean;

  // Actions
  setAuthenticated: (user: AuthUser) => void;
  updateUser: (partial: Partial<AuthUser>) => void;
  clearAuth: (sessionExpired?: boolean) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  initializeAuth: () => Promise<void>;
  resetAuth: () => void;
}

export interface TokenService {
  exchangeToken: (originalToken: string, platform: string) => Promise<AuthResponse>;
  refreshAuthToken: () => Promise<RefreshResponse>;
  fetchCurrentUser: () => Promise<AuthUser>;
  logout: () => Promise<void>;
  logoutAll: () => Promise<void>;
}

/**
 * App-level user schema.
 *
 * Extends the backend's BaseUserDto (MeResponse) with app-level fields
 * that are not yet in the backend spec but are used in the UI.
 *
 * When the backend adds these fields, remove them from the extension below
 * and re-run `pnpm types:api:generate`.
 */
export const authUserSchema = MeResponse.extend({
  // App-level extensions — not in BaseUserDto yet
  companyName: z.string().nullish().optional(),
  teamMemberCount: z.number().nullish().optional(),
});

export type AuthUser = z.infer<typeof authUserSchema>;
export const authResponseSchema = z.object({
  expiresIn: z.number().optional(),
  user: authUserSchema,
});
export type AuthResponse = z.infer<typeof authResponseSchema>;
export const refreshResponseSchema = z.object({ expiresIn: z.number() });
export type RefreshResponse = z.infer<typeof refreshResponseSchema>;
